import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { alertSuccess, alertError } from '../../components/Toast';
import useFinancialDashboardData from '../../core/hooks/useFinancialDashboardData';
import { formatCurrency } from '../../core/utils/format';
import { getPeriodOptions } from '../../core/utils/financialPeriod';
import { ENTRY_TYPE } from '../../core/constants/financial';
import { resetCompanyFinancialData } from '../../core/utils/financialReset';
import FinancialTopbar from './components/FinancialTopbar';
import QuickAccessCard from './components/QuickAccessCard';
import SummaryCard from './components/SummaryCard';
import BancosCard from './components/BancosCard';
import RevenueChart from './components/RevenueChart';
import BankModal from './components/BankModal';
import AccountPlanModal from './components/AccountPlanModal';
import CostCenterModal from './components/CostCenterModal';
import FinancialEntryModal from './components/FinancialEntryModal';
import FinancialEntriesListModal from './components/FinancialEntriesListModal';
import FinancialEntriesDetailModal from './components/FinancialEntriesDetailModal';
import AccountsPayableModal from './components/AccountsPayableModal';
import AccountsReceivableModal from './components/AccountsReceivableModal';
import CashFlowModal from './components/CashFlowModal';
import DREModal from './components/DREModal';
import ProjectedCashFlowModal from './components/ProjectedCashFlowModal';
import ProjectedCashFlowPanel from './components/ProjectedCashFlowPanel';

const FinancialDashboardScreen = ({ companies = [], selectedCompany = {}, changeCompany }) => {
  const user = useSelector((state) => state.app.user || {});
  const periodOptions = useMemo(() => getPeriodOptions(), []);
  const [openModal, setOpenModal] = useState(null);
  const [entryOpen, setEntryOpen] = useState(false);
  const [entriesListOpen, setEntriesListOpen] = useState(false);
  const [detailType, setDetailType] = useState(null); // ENTRY_TYPE.RECEITA | DESPESA | null
  const [payableOpen, setPayableOpen] = useState(false);
  const [receivableOpen, setReceivableOpen] = useState(false);
  const [cashFlowOpen, setCashFlowOpen] = useState(false);
  const [dreOpen, setDreOpen] = useState(false);
  const [projectedOpen, setProjectedOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(periodOptions[0]);
  const [refreshKey, setRefreshKey] = useState(0);

  // Cards "Contas a Pagar/Receber" usam exclusivamente o período do dashboard
  // (selectedPeriod). Filtros aplicados dentro dos modais de listagem são
  // locais e NÃO afetam o dashboard (SON-33: sem herança de filtro entre telas).
  const dashboardRange = selectedPeriod?.range;

  const {
    loading,
    banks,
    totalBalance,
    receitas,
    despesas,
    result,
    chartData,
    aPagar,
    aReceber,
  } = useFinancialDashboardData({
    companyId: selectedCompany?.id,
    periodRange: selectedPeriod?.range,
    payableRange: dashboardRange,
    receivableRange: dashboardRange,
    refreshKey,
  });

  const handleCadastroClick = (key) => setOpenModal(key);

  const handleResetClick = async () => {
    if (!selectedCompany?.id) {
      alertError('Selecione uma empresa antes de resetar.');
      return;
    }
    const result = await Swal.fire({
      title: 'Reset geral dos dados financeiros',
      html: `
        <p style="margin-bottom:14px;">Esta ação <strong style="color:#e84040;">apaga permanentemente</strong> todos os dados financeiros de <strong>${selectedCompany.name || 'esta empresa'}</strong>:</p>
        <ul style="text-align:left; margin: 0 auto; display:inline-block; font-size:14px; line-height:1.6;">
          <li>Bancos cadastrados</li>
          <li>Centros de custo</li>
          <li>Lançamentos financeiros</li>
          <li>Parcelas (a pagar e a receber, pagas ou pendentes)</li>
          <li>Plano de contas (será recriado com o padrão)</li>
        </ul>
        <p style="margin-top:14px; font-size:13px;">Para confirmar, digite <strong>RESETAR</strong> abaixo:</p>
      `,
      icon: 'warning',
      input: 'text',
      inputPlaceholder: 'Digite RESETAR',
      inputAttributes: {
        autocapitalize: 'characters',
        spellcheck: 'false',
      },
      inputValidator: (value) => {
        if (value !== 'RESETAR') return 'Você precisa digitar RESETAR (em maiúsculas) para confirmar.';
      },
      customClass: { input: 'fd-swal-reset-input' },
      showCancelButton: true,
      showConfirmButton: false,
      showDenyButton: true,
      denyButtonText: 'Apagar tudo',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    });

    if (result.isDenied) {
      const { error } = await resetCompanyFinancialData(selectedCompany.id, user.id);
      if (error) {
        alertError('Erro ao resetar dados financeiros.');
        return;
      }
      alertSuccess('Dados financeiros apagados!');
      setRefreshKey((k) => k + 1);
    }
  };

  const banksForCard = banks.map((b) => ({
    name: b.name,
    amount: formatCurrency(b.currentBalance),
  }));

  const isLucro = result >= 0;
  // Saldo projetado = saldo atual + receitas pendentes - despesas pendentes (RN03).
  const saldoProjetado = totalBalance + aReceber - aPagar;

  return (
    <div className="financial-dashboard">
      <FinancialTopbar
        companies={companies}
        selectedCompany={selectedCompany}
        changeCompany={changeCompany}
        onCadastroClick={handleCadastroClick}
        onLancamentoClick={() => setEntriesListOpen(true)}
        onResetClick={handleResetClick}
        selectedPeriod={selectedPeriod}
        onPeriodChange={setSelectedPeriod}
      />

      <div className="fd-content">
        <div className="fd-quick-row">
          <QuickAccessCard label="Contas a Pagar" value={formatCurrency(aPagar)} valueColor="orange" period={selectedPeriod?.label} accent="orange" loading={loading} onClick={() => setPayableOpen(true)} />
          <QuickAccessCard label="Contas a Receber" value={formatCurrency(aReceber)} valueColor="green" period={selectedPeriod?.label} accent="green" loading={loading} onClick={() => setReceivableOpen(true)} />
          <QuickAccessCard
            label="Fluxo de Caixa"
            value={formatCurrency(totalBalance)}
            valueLabel="Saldo Atual"
            projected={formatCurrency(saldoProjetado)}
            projectedColor={saldoProjetado >= 0 ? 'green' : 'orange'}
            period={selectedPeriod?.label}
            loading={loading}
            onClick={() => setCashFlowOpen(true)}
          />
          <BancosCard total={formatCurrency(totalBalance)} banks={banksForCard} loading={loading} />
        </div>

        <div className="fd-cards-row">
          <SummaryCard
            label="Receitas do mês"
            value={formatCurrency(receitas)}
            color="green"
            loading={loading}
            onClick={() => setDetailType(ENTRY_TYPE.RECEITA)}
          />
          <SummaryCard
            label="Despesas do mês"
            value={formatCurrency(despesas)}
            color="orange"
            loading={loading}
            onClick={() => setDetailType(ENTRY_TYPE.DESPESA)}
          />
          <SummaryCard label="Resultado" value={formatCurrency(result)} color="highlight" loading={loading}>
            <div className="fd-card-sub">
              <span className={`fd-badge ${isLucro ? 'lucro' : 'prejuizo'}`}>
                {isLucro ? 'LUCRO' : 'PREJUÍZO'}
              </span>
            </div>
          </SummaryCard>
          <SummaryCard label="DRE" value="Ver relatório" color="blue" loading={loading} onClick={() => setDreOpen(true)} />
        </div>

        <RevenueChart data={chartData} loading={loading} />

        <ProjectedCashFlowPanel companyId={selectedCompany?.id} refreshKey={refreshKey} />
      </div>

      <BankModal
        isOpen={openModal === 'banco'}
        onClose={() => setOpenModal(null)}
        onChanged={() => setRefreshKey((k) => k + 1)}
      />
      <AccountPlanModal isOpen={openModal === 'plano-contas'} onClose={() => setOpenModal(null)} />
      <CostCenterModal isOpen={openModal === 'centro-custo'} onClose={() => setOpenModal(null)} />
      <FinancialEntryModal
        isOpen={entryOpen}
        onClose={() => setEntryOpen(false)}
        onSaved={() => setRefreshKey((k) => k + 1)}
      />
      <FinancialEntriesListModal
        isOpen={entriesListOpen}
        onClose={() => setEntriesListOpen(false)}
        onChanged={() => setRefreshKey((k) => k + 1)}
      />
      <FinancialEntriesDetailModal
        isOpen={detailType != null}
        onClose={() => setDetailType(null)}
        periodRange={selectedPeriod?.range}
        type={detailType}
        onChanged={() => setRefreshKey((k) => k + 1)}
      />
      <AccountsPayableModal
        isOpen={payableOpen}
        onClose={() => setPayableOpen(false)}
        onChanged={() => setRefreshKey((k) => k + 1)}
      />
      <AccountsReceivableModal
        isOpen={receivableOpen}
        onClose={() => setReceivableOpen(false)}
        onChanged={() => setRefreshKey((k) => k + 1)}
      />
      <CashFlowModal
        isOpen={cashFlowOpen}
        onClose={() => setCashFlowOpen(false)}
        onNewEntry={() => setEntryOpen(true)}
        onChanged={() => setRefreshKey((k) => k + 1)}
        onOpenProjected={() => {
          setCashFlowOpen(false);
          setProjectedOpen(true);
        }}
      />
      <DREModal
        isOpen={dreOpen}
        onClose={() => setDreOpen(false)}
      />
      <ProjectedCashFlowModal
        isOpen={projectedOpen}
        onClose={() => setProjectedOpen(false)}
      />
    </div>
  );
};

export default FinancialDashboardScreen;
